import {useEffect, useState} from 'react'
import _ from 'lodash'
import {coordsFields, embeddingFields} from '@/utils/media'

export type fieldTypes =
  | 'boolean'
  | 'string'
  | 'number'
  | 'string_array'
  | 'coords'
  | 'embedding'
  | 'number_array'
  | 'categorical_scores'
  | 'remote_resource'
  | 'image_array'
  | 'generic_object'

export interface IFieldTypeMap {
  [key: string]: fieldTypes
}

export interface ICountMap {
  [key: string]: number
}

export interface ICategoryCounts {
  [key: string]: ICountMap
}

export interface INumberStats {
  [key: string]: {
    isInteger: boolean
    min: number
    max: number
  }
}

export interface IDataSourceStats {
  fieldTypes: IFieldTypeMap
  fields: ICountMap
  types: ICountMap
  categories: ICategoryCounts
  numbers: INumberStats
}

export function mapFieldTypes(items: any[]): IFieldTypeMap {
  const res = {}
  function detectFieldValueType(key: string, val: any): fieldTypes | undefined {
    if (val === undefined || val === null) return
    if (_.isBoolean(val)) return 'boolean'
    if (_.isString(val)) return 'string'
    if (_.isFinite(val)) {
      // if previously detected as a string don't allow to redetect as a number
      if (_.get(res, key) === 'string') return 'string'
      return 'number'
    }
    if (_.isArray(val)) {
      const _valTypes = _.chain(val)
        .compact()
        .map((v) => detectFieldValueType('', v))
        .value()
      const _valTypesUniq = _.uniq(_valTypes)
      if (_valTypesUniq.length === 1) {
        if (_valTypesUniq[0] === 'string') return 'string_array'
        if (_valTypesUniq[0] === 'number') {
          // if (_valTypes.length === 2) return 'coords';
          if (_.includes(coordsFields, key)) return 'coords'
          if (_.includes(embeddingFields, key)) return 'embedding'
          return 'number_array'
        }
        if (_valTypesUniq[0] === 'remote_resource') {
          if (key === 'backdrops') return 'image_array'
        }
      }
      // console.log('unkown array', key, _valTypes);
      // return 'array';
    }
    if (_.isPlainObject(val)) {
      if (_.keys(val).length === 0) return
      const _valTypes = _.chain(val)
        .toPairs()
        .compact()
        .map(([k, v]) => [k, detectFieldValueType(k, v)])
        .uniq()
        .fromPairs()
        .value()
      const _values = _.chain(_valTypes).values().uniq().value()
      if (_values.length === 1 && _values[0] === 'number') return 'categorical_scores'
      if (_.get(_valTypes, 'url') === 'string') return 'remote_resource'
      // console.log('unkown object', key, _valTypes);
      return 'generic_object'
    }
  }
  _.forEach(
    // TODO: limit items checked?
    // limitCheckItems ? _.slice(items, 0, limitCheckItems) : items,
    items,
    (item) => {
      _.forEach(_.toPairs(item), ([key, val]) => {
        const t = detectFieldValueType(key, val)
        if (t) _.set(res, key, t)
        else if (!_.has(res, key)) _.set(res, key, undefined)
      })
    }
  )
  return res
}

// TODO: utils?
export function calculateDataSourceStats(items: any[]): IDataSourceStats {
  const fieldTypes = mapFieldTypes(items)
  const categorical_keys = _.chain(fieldTypes)
    .toPairs()
    .filter(([, v]) => _.includes(['categorical_scores', 'string_array'], v))
    .map(([k, _v]) => k)
    .value()
  const number_keys = _.chain(fieldTypes)
    .toPairs()
    .filter(([, v]) => _.includes(['number'], v))
    .map(([k, _v]) => k)
    .value()
  const fields: ICountMap = {}
  const types: ICountMap = {}
  const categories: ICategoryCounts = {}
  const numbers: INumberStats = {}
  _.chain(items)
    .forEach((c) => {
      if (!c) return

      // fields
      _.chain(_.keys(c))
        .forEach((fk) => {
          const fv = _.get(fields, fk) || 0
          _.set(fields, fk, fv + 1)
        })
        .value()

      // types
      if (c.type) {
        const tv = _.get(types, c.type) || 0
        _.set(types, c.type, tv + 1)
      }

      // numbers
      _.chain(number_keys)
        .forEach((nk) => {
          const nv = _.get(numbers, nk) || {
            isInteger: true,
            min: Number.POSITIVE_INFINITY,
            max: Number.NEGATIVE_INFINITY,
          }
          const val = _.get(c, nk)
          if (val === undefined) return
          _.set(numbers, nk, {
            isInteger: nv.isInteger && val % 1 === 0,
            min: _.min([nv.min, val]),
            max: _.max([nv.max, val]),
          })
        })
        .value()

      // categories
      _.chain(categorical_keys)
        .forEach((ck) => {
          const cv = _.get(categories, ck) || {}
          let k = _.get(c, ck, [])
          if (_.isPlainObject(k)) k = _.keys(k)
          if (k === null) return
          if (_.isArray(k)) {
            _.forEach(k, (kk) => {
              cv[kk] = (_.get(cv, kk) || 0) + 1
            })
          } else if (_.isString(k) || _.isNumber(k)) {
            cv[k] = (_.get(cv, k) || 0) + 1
          } else {
            console.error('Unkown value type:', k)
            return
          }
          _.set(categories, ck, cv)
        })
        .value()
    })
    .value()

  return {
    fieldTypes,
    fields,
    types,
    categories,
    numbers,
  }
}

export function useDataSourceStats(items: any[]): IDataSourceStats {
  const [stats, setStats] = useState<IDataSourceStats>({
    fieldTypes: {},
    categories: {},
    fields: {},
    types: {},
    numbers: {},
  })

  useEffect(() => {
    const _stats = calculateDataSourceStats(items)
    if (_.isEqual(_stats, stats)) return
    setStats((prev) => (_.isEqual(prev, _stats) ? prev : _stats))
  }, [items])

  return stats
}
